import { useEffect, useState } from 'react';

import { usePlatformTableRowByIdTeam } from '@cloudquery/api-platform';
import { useBaseAuthenticated } from '@cloudquery/base-users';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';

import toast from 'react-hot-toast';

import {
  DetailsDrawer,
  CLOUD_ASSET_TABLE_NAME,
  getErrorMessage,
  CloseButton,
  CloudDisplay,
} from 'shared/base';

import { ResourceDetailActionsMenu } from './actionsMenu';
import { RelatedResources } from './relatedResources';
import { ResourceDetails } from './resourceDetails';
import { useTableStateContext } from '../../../hooks/useTableStateContext';
import { AssetInventoryTableRow } from '../../../types';

interface Props {
  recordId?: string;
}

export const AssetInventoryDetailView = ({ recordId }: Props) => {
  const { currentMembership } = useBaseAuthenticated();
  const { columns, detailIds, setDetailIds } = useTableStateContext();
  // Initialize to true since this component is only rendered when recordId exists
  const [modalOpen, setModalOpen] = useState<boolean>(true);

  const { data, error } = usePlatformTableRowByIdTeam(
    currentMembership.team.name,
    CLOUD_ASSET_TABLE_NAME,
    recordId!,
    undefined,
    {
      query: {
        enabled: !!recordId,
      },
    },
  );

  const record = data?.data?.data as unknown as AssetInventoryTableRow;

  const [tab, setTab] = useState<string>('details');

  useEffect(() => {
    if (error) {
      toast.error(getErrorMessage(error));
    }
  }, [error]);

  const onClosedProxy = () => {
    setTab('details');

    const copiedDetailIds = [...(detailIds ?? [])];
    copiedDetailIds.pop();
    setDetailIds(copiedDetailIds);
  };

  const onClose = () => setModalOpen(false);

  return (
    <DetailsDrawer
      anchor="right"
      ModalProps={{
        onTransitionExited: onClosedProxy,
      }}
      onClose={onClose}
      open={modalOpen}
      slotProps={{
        paper: {
          sx: {
            width: {
              lg: '70vw',
              xs: '100%',
            },
          },
        },
      }}
    >
      <Stack height="100%" spacing={3}>
        <Stack gap={2} width="100%">
          <Box alignItems="center" display="flex" gap={2}>
            <CloudDisplay value={record?.cloud} />
            <Stack direction="row" justifyContent="space-between" width="100%">
              <Stack>
                <Typography variant="body1Bold">{record?.name}</Typography>
                <Typography variant="body2">{record?.resource_type_label}</Typography>
              </Stack>
              <Box alignItems="center" display="flex">
                <ResourceDetailActionsMenu record={record} />
                <CloseButton onClose={onClose} />
              </Box>
            </Stack>
          </Box>
          <Tabs onChange={(_, newTab) => setTab(newTab)} value={tab}>
            <Tab label="Resource details" value={'details'} />
            {/* <Tab label="Related resources" value={'relations'} /> */}
          </Tabs>
        </Stack>
        <Box sx={{ height: 'calc(100vh - 220px)' }}>
          {tab === 'details' && (
            <ResourceDetails columns={columns} onClose={onClose} record={record} />
          )}
          {tab === 'relations' && <RelatedResources record={record} />}
        </Box>
      </Stack>
    </DetailsDrawer>
  );
};
